import { motion } from "framer-motion";
import { EASE } from "./Reveal";

export function parseBlocks(content = "") {
  return content
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean)
    .map((block) => {
      if (block.startsWith("## ")) return { type: "h2", text: block.slice(3) };
      if (block.startsWith("> ")) return { type: "quote", text: block.slice(2) };
      if (block.startsWith("img:")) {
        const [src, caption] = block.slice(4).split("|").map((s) => s && s.trim());
        return { type: "image", src, caption };
      }
      if (block.startsWith("*") && block.endsWith("*")) return { type: "italic", text: block.slice(1, -1) };
      return { type: "p", text: block };
    });
}

export default function ContentBlocks({ content }) {
  return (
    <div data-testid="content-blocks">
      {parseBlocks(content).map((b, i) => {
        if (b.type === "h2") {
          return (
            <motion.h2
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: EASE }}
              className="font-display uppercase text-2xl md:text-4xl text-ink mt-14 mb-6 leading-tight"
            >
              {b.text}
            </motion.h2>
          );
        }
        if (b.type === "quote") {
          return (
            <motion.blockquote
              key={i}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: EASE }}
              className="my-12 rounded-[2rem] bg-blush p-8 md:p-12"
            >
              <p className="font-elegant italic text-2xl md:text-3xl leading-snug text-ink">{b.text}</p>
            </motion.blockquote>
          );
        }
        if (b.type === "image") {
          return (
            <motion.figure
              key={i}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: EASE }}
              className="my-10 group"
            >
              <div className="overflow-hidden rounded-[1.75rem]">
                <img
                  src={b.src}
                  alt={b.caption || "Editorial image"}
                  className="w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              {b.caption && (
                <figcaption className="mt-3 text-[10px] font-bold uppercase tracking-[0.2em] text-gold">{b.caption}</figcaption>
              )}
            </motion.figure>
          );
        }
        if (b.type === "italic") {
          return (
            <p key={i} className="font-elegant italic text-2xl md:text-3xl text-center text-ink/80 my-8">
              {b.text}
            </p>
          );
        }
        return (
          <p key={i} className="text-base md:text-lg leading-relaxed text-neutral-700 mb-6">
            {b.text}
          </p>
        );
      })}
    </div>
  );
}
